import type { ReactElement, ReactNode } from 'react';
import { ResponsiveContainer } from 'recharts';

// The app's chart look (axis ticks, grid and tooltip in the theme's chart and tooltip colours).
export const tickStyle = { fontSize: 11, fill: 'var(--color-chart-tick)' };
export const gridStroke = 'var(--color-chart-grid)';
export const tooltipStyle = {
  background: 'var(--color-tooltip-bg)',
  color: 'var(--color-tooltip-text)',
  border: 'none',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 12,
  boxShadow: '0 8px 24px -12px rgba(0,0,0,0.25)',
};
export const tooltipItemStyle = { color: 'var(--color-tooltip-text)', padding: 0 };
export const tooltipLabelStyle = { color: 'var(--color-tooltip-text)', fontWeight: 600, marginBottom: 4 };
export const axisLabelStyle = { fontSize: 11, fill: 'var(--color-secondary-600)' };

export type MarkerShape = 'none' | 'circle' | 'square' | 'diamond';

export function Marker({ shape, x, y, color }: { shape: MarkerShape; x: number; y: number; color: string }) {
  const ring = { stroke: 'var(--color-primary-contrast)', strokeWidth: 1.5 };
  if (shape === 'circle')
    return <circle cx={x} cy={y} r={4} fill={color} {...ring} />;
  if (shape === 'square')
    return <rect x={x - 3.5} y={y - 3.5} width={7} height={7} fill={color} {...ring} />;
  if (shape === 'diamond')
    return <path d={`M${x} ${y - 5}L${x + 5} ${y}L${x} ${y + 5}L${x - 5} ${y}Z`} fill={color} {...ring} />;
  return null;
}

export function LegendSwatch({ color, marker = 'none', dash, width = 2, hollowDot = false }: {
  color: string;
  marker?: MarkerShape;
  dash?: string;
  width?: number;
  hollowDot?: boolean;
}) {
  return (
    <svg aria-hidden width={22} height={12} viewBox="0 0 22 12" className="shrink-0 overflow-visible">
      {hollowDot
        ? <circle cx={11} cy={6} r={4} fill="var(--color-primary-contrast)" stroke={color} strokeWidth={2} />
        : <line x1={1} y1={6} x2={21} y2={6} stroke={color} strokeWidth={width} strokeLinecap={dash === undefined ? 'round' : 'butt'} strokeDasharray={dash} />}
      <Marker shape={marker} x={11} y={6} color={color} />
    </svg>
  );
}

export function LegendItem({ swatch, children }: { swatch: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="inline-flex">{swatch}</span>
      {children}
    </span>
  );
}

// `size` sets the height; the default suits the main charts.
export function ChartBox({ size = 'h-60 @xl:h-[280px]', children }: { size?: string; children: ReactElement }) {
  return (
    <div className={`${size} w-full min-w-0`}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}
